import { makeObservable, observable, action, flow, computed } from "mobx";
import {
  getWorkspaceList,
  getWorkspaceById,
  createWorkspace,
  uploadTenantPhoto,
  updateWorkspace,
  activateWorkspace as activateWorkspaceService,
  deactivateWorkspace as deactivateWorkspaceService,
	getAuditList
} from "../../../services/workspace";

class WorkspaceStore {
  selectedWorkspaceId = null;
  selectedWorkspace = null;
  currentWorkspace = null;
  workspaceList = [];
  meta = null;
  isLoading = false;
  error = false;
  errorMessage = "";
  
  // Audit state
  auditList = [];
  auditMeta = null;

  // Pagination state
  currentPage = 1;
  itemsPerPage = 15;
  searchParams = {};
  sortParams = {
    order_by: ["created_at"],
    order_direction: ["desc"],
  };

  constructor() {
    makeObservable(this, {
      selectedWorkspaceId: observable,
      selectedWorkspace: observable,
      currentWorkspace: observable,
      workspaceList: observable,
      meta: observable,
      isLoading: observable,
      error: observable,
      errorMessage: observable,
      auditList: observable,
      auditMeta: observable,
      currentPage: observable,
      itemsPerPage: observable,
      searchParams: observable,
      sortParams: observable,
      
      setSelectedWorkspace: action,
      setSelectedWorkspaceId: action,
      setCurrentWorkspace: action,
      clearSelectedWorkspace: action,
      clear: action,
      setError: action,
      clearError: action,
      setLoading: action,
      setWorkspaceList: action,
      setMeta: action,
      setAuditList: action,
      setAuditMeta: action,
      setCurrentPage: action,
      setItemsPerPage: action,
      setSearchParams: action,
      setSortParams: action,
      resetPagination: action,
      handleApiResponse: action,
      handleApiError: action,
      
      fetchWorkspaceList: flow,
      fetchPage: flow,
      fetchWorkspaceById: flow,
      createWorkspace: flow,
      updateWorkspace: flow,
      uploadTenantPhoto: flow,
      fetchAuditList: flow,
      activateWorkspace: flow,
      deactivateWorkspace: flow,

      hasNextPage: computed,
      hasPreviousPage: computed,
    });
  }

  /**
   * Sets the selected workspace with full data
   * @param {Object} workspace - Workspace object with id, name, and other properties
   */
  setSelectedWorkspace(workspace) {
    if (workspace) {
      // Evaluate priority values once and reuse them (performance optimization)
      // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic)
      const workspaceName = workspace.workspaceName || workspace.workspace_name || workspace.tenantName || workspace.tenant_name || workspace.name;
      const tenantUuid = workspace.tenant_uuid || workspace.tenantUuid;
      const workspaceUuid = workspace.workspace_uuid || workspace.workspaceUuid || tenantUuid;
      const contactEmail = workspace.contact_email || workspace.contactEmail || workspace.ownerEmail || workspace.owner_email;
      const contactName = workspace.contact_name || workspace.contactName || workspace.ownerName || workspace.owner_name;
      const ownerName = workspace.owner_name || workspace.ownerName || contactName;
      const ownerEmail = workspace.owner_email || workspace.ownerEmail || contactEmail;
      const phoneNumber = workspace.phone_number || workspace.phoneNumber;
      const billingPlan = workspace.billing_plan || workspace.billingPlan;
      const billingStatus = workspace.billing_status || workspace.billingStatus;
      const subscriptionStart = workspace.subscription_start || workspace.subscriptionStart;
      const subscriptionEnd = workspace.subscription_end || workspace.subscriptionEnd;
      const isActive = workspace.isActive !== undefined ? workspace.isActive : workspace.is_active;
      const address = workspace.address || workspace.contact_address || null;
      const createdAt = workspace.created_at || workspace.createdAt;
      const updatedAt = workspace.updated_at || workspace.updatedAt;
      const ownerId = workspace.owner_id || workspace.ownerId;
      const photoUrl = workspace.photo_url || workspace.photoUrl || null;
      const userActiveCount = workspace.user_active_count || workspace.userActiveCount || 0;
      const userCount = workspace.user_count || workspace.userCount || 0;

      // Map API response fields (camelCase) to snake_case for consistency
      // Reuse pre-evaluated values to avoid redundant property access
      const mappedWorkspace = {
        ...workspace,
        // UUIDs
        tenant_uuid: tenantUuid,
        tenantUuid: tenantUuid,
        workspace_uuid: workspaceUuid,
        workspaceUuid: workspaceUuid,
        
        // Workspace name - reuse evaluated value (all formats for backward compatibility)
        workspaceName,
        workspace_name: workspace.workspace_name || workspace.workspaceName || workspace.tenant_name || workspace.tenantName || workspace.name,
        name: workspaceName,
        tenant_name: workspace.workspace_name || workspace.workspaceName || workspace.tenant_name || workspace.tenantName || workspace.name,
        tenantName: workspaceName,
        
        // Contact/Owner info - reuse evaluated values
        contact_email: contactEmail,
        contactEmail,
        contact_name: contactName,
        contactName,
        owner_name: ownerName,
        ownerName,
        owner_email: ownerEmail,
        ownerEmail,
        
        // Other fields - reuse evaluated values
        phone_number: phoneNumber,
        phoneNumber,
        billing_plan: billingPlan,
        billing_status: billingStatus,
        subscription_start: subscriptionStart,
        subscription_end: subscriptionEnd,
        // isActive field - ensure both snake_case and camelCase are available
        is_active: workspace.is_active !== undefined ? workspace.is_active : isActive,
        isActive,
        status: workspace.status,
        address,
        contact_address: workspace.contact_address || address,
        created_at: createdAt,
        createdAt,
        updated_at: updatedAt,
        updatedAt,
        owner_id: ownerId,
        // photo_url will be null/undefined for now, avatarUtils will handle it
        photo_url: photoUrl,
        photoUrl,
        // Statistics fields
        user_active_count: userActiveCount,
        userActiveCount,
        user_count: userCount,
        userCount,
        agents: workspace.agents || 0,
        workflows: workspace.workflows || 0,
        campaigns: workspace.campaigns || 0,
      };
      
      this.selectedWorkspace = mappedWorkspace;
      // Backend returns tenant_uuid or tenantUuid, but we also support workspace_uuid/workspaceUuid for backward compatibility
      this.selectedWorkspaceId = mappedWorkspace.tenant_uuid || mappedWorkspace.tenantUuid || mappedWorkspace.workspace_uuid || mappedWorkspace.workspaceUuid;
      this.error = false;
      this.errorMessage = "";
    }
  }

  /**
   * Setea el workspace actual (currentWorkspace)
   * @param {Object|null} workspace - Workspace actual
   * @param {Object} authStore - AuthStore instance para sincronización (opcional)
   */
  setCurrentWorkspace(workspace, authStore = null) {
    if (!workspace) {
      this.currentWorkspace = null;
      return;
    }
    
    // Mapear workspaceUuid a tenant_uuid si no existe (la API devuelve workspaceUuid)
    const workspaceUuid = workspace.workspaceUuid || workspace.workspace_uuid;
    const tenantUuid = workspace.tenant_uuid || workspace.tenantUuid || workspaceUuid;
    
    // Crear workspace mapeado con todos los campos necesarios
    const mappedWorkspace = {
      ...workspace,
      // Asegurar que tenant_uuid esté disponible (mapear desde workspaceUuid si es necesario)
      tenant_uuid: tenantUuid,
      tenantUuid: tenantUuid,
      workspace_uuid: workspaceUuid || tenantUuid,
      workspaceUuid: workspaceUuid || tenantUuid,
    };
    
    this.currentWorkspace = mappedWorkspace;
    
    // Sincronizar tenant_uuid con AuthStore si está disponible
    if (authStore && tenantUuid) {
      authStore.syncWorkspaceUuidFromWorkspace(tenantUuid);
    }
  }

  /**
   * Sets only the workspace ID (useful when navigating before fetching full data)
   * @param {string} workspaceId - Workspace ID/UUID
   */
  setSelectedWorkspaceId(workspaceId) {
    this.selectedWorkspaceId = workspaceId;
    // Don't clear selectedWorkspace if it matches the ID
    if (this.selectedWorkspace && 
        (this.selectedWorkspace.tenant_uuid !== workspaceId &&
         this.selectedWorkspace.tenant_id !== workspaceId &&
         this.selectedWorkspace.workspaceUuid !== workspaceId && 
         this.selectedWorkspace.id !== workspaceId && 
         this.selectedWorkspace.uuid !== workspaceId)) {
      this.selectedWorkspace = null;
    }
  }

  /**
   * Clears the selected workspace
   */
  clearSelectedWorkspace() {
    this.selectedWorkspaceId = null;
    this.selectedWorkspace = null;
    this.error = false;
    this.errorMessage = "";
  }

  /**
   * Limpia todo el estado del workspace (útil para logout)
   */
  clear() {
    this.selectedWorkspaceId = null;
    this.selectedWorkspace = null;
    this.currentWorkspace = null;
    this.workspaceList = [];
    this.meta = null;
    this.isLoading = false;
    this.error = false;
    this.errorMessage = "";
    this.auditList = [];
    this.auditMeta = null;
    this.resetPagination();
  }

  /**
   * Sets loading state
   * @param {boolean} loading - Loading state
   */
  setLoading(loading) {
    this.isLoading = loading;
  }

  /**
   * Sets error state with message
   * @param {boolean} hasError - Whether there is an error
   * @param {string} message - Error message (optional)
   */
  setError(hasError, message = "") {
    this.error = hasError;
    this.errorMessage = message || "";
  }

  /**
   * Clears error state
   */
  clearError() {
    this.error = false;
    this.errorMessage = "";
  }

  /**
   * Sets workspace list
   * @param {Array} workspaces - Array of workspace objects
   */
  setWorkspaceList(workspaces) {
    this.workspaceList = Array.isArray(workspaces) ? workspaces : [];
  }

  /**
   * Sets meta information
   * @param {Object} meta - Meta object with pagination info
   */
  setMeta(meta) {
    this.meta = meta;
  }

  /**
   * Sets audit list
   * @param {Array} auditEvents - Array of audit event objects
   */
  setAuditList(auditEvents) {
    this.auditList = Array.isArray(auditEvents) ? auditEvents : [];
  }

  /**
   * Sets audit meta information
   * @param {Object} meta - Meta object with pagination info
   */
  setAuditMeta(meta) {
    this.auditMeta = meta;
  }

  /**
   * Gets the workspace name (from selectedWorkspace or fallback)
   * @returns {string} Workspace name
   */
  getWorkspaceName() {
    if (this.selectedWorkspace) {
      // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
      return this.selectedWorkspace.workspaceName || 
             this.selectedWorkspace.workspace_name || 
             this.selectedWorkspace.tenantName || 
             this.selectedWorkspace.tenant_name || 
             this.selectedWorkspace.name || 
             this.selectedWorkspace.title || 
             "Workspace";
    }
    return "Workspace";
  }

  /**
   * Sets the current page
   * @param {number} page - Page number
   */
  setCurrentPage(page) {
    this.currentPage = page;
  }

  /**
   * Sets items per page and resets to first page
   * @param {number} size - Number of items per page
   */
  setItemsPerPage(size) {
    this.itemsPerPage = size;
    this.currentPage = 1; // Reset to first page when changing items per page
  }

  /**
   * Sets search parameters and resets to first page
   * @param {Object} params - Search parameters (e.g., { keyword: "value" })
   */
  setSearchParams(params) {
    this.searchParams = params && Object.keys(params).length > 0 ? params : {};
    this.currentPage = 1;
  }

  /**
   * Sets sort parameters and resets to first page
   * @param {Object} params - Sort parameters (order_by, order_direction)
   */
  setSortParams(params) {
    this.sortParams = params;
    this.currentPage = 1; // Reset to first page when sorting
  }

  /**
   * Resets pagination to initial state
   */
  resetPagination() {
    this.currentPage = 1;
    this.itemsPerPage = 15;
    this.searchParams = {};
    this.sortParams = {
      order_by: ["created_at"],
      order_direction: ["desc"],
    };
  }

  /**
   * Checks if there's a next page
   * @returns {boolean}
   */
  get hasNextPage() {
    return this.meta?.has_next || false;
  }

  /**
   * Checks if there's a previous page
   * @returns {boolean}
   */
  get hasPreviousPage() {
    return this.currentPage > 1;
  }

  /**
   * Handles API response and extracts error messages
   * @param {Object} response - API response
   * @param {string} defaultErrorMessage - Default error message if extraction fails
   * @returns {Object} Parsed response with statusCode, msgCode, body, meta
   * @throws {Error} If response indicates an error
   */
  handleApiResponse(response, defaultErrorMessage) {
    if (!response) {
      throw new Error('Invalid response from server');
    }

    // Handle both snake_case and camelCase formats
    const statusCode = response.status_code || response.statusCode || 200;
    const msgCode = response.msg_code || response.msgCode || [];
    const responseBody = response.body;
    const meta = response.meta || null;

    // Success (2xx)
    if (statusCode >= 200 && statusCode < 300) {
      return {
        statusCode,
        msgCode,
        body: responseBody,
        meta,
      };
    }

    // Error - extract message from msgCode (combine all messages)
    let errorMessage = defaultErrorMessage;
    if (msgCode.length > 0) {
      // Combine all error messages from msgCode array
      errorMessage = msgCode
        .map(msg => msg.description || msg.code)
        .filter(Boolean)
        .join(', ') || defaultErrorMessage;
    }

    this.setError(true, errorMessage);
    
    const error = new Error(errorMessage);
    error.response = {
      data: response,
      status: statusCode,
    };
    
    throw error;
  }

  /**
   * Handles caught errors from API calls
   * @param {Error} error - Caught error
   * @param {string} defaultErrorMessage - Default error message
   * @throws {Error} Processed error
   */
  handleApiError(error, defaultErrorMessage) {
    // If error already has a message from handleApiResponse, use it
    if (error.message && error.message !== defaultErrorMessage) {
      // Error message was already set by handleApiResponse
      // Just ensure error state is set (it should already be)
      if (!this.error) {
        this.setError(true, error.message);
      }
      throw error;
    }

    // Handle network errors or API errors
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      
      // Try to extract message from msgCode array (API format)
      let errorMessage = defaultErrorMessage;
      const msgCode = responseData.msg_code || responseData.msgCode || [];
      if (msgCode.length > 0) {
        // Combine all error messages from msgCode
        errorMessage = msgCode
          .map(msg => msg.description || msg.code)
          .filter(Boolean)
          .join(', ') || defaultErrorMessage;
      } else {
        // Fallback to other message fields
        errorMessage = responseData.message || error.message || defaultErrorMessage;
      }

      this.setError(true, errorMessage);
      
      const apiError = new Error(errorMessage);
      apiError.response = {
        data: responseData,
        status: error.response.status,
      };
      
      throw apiError;
    }

    // Handle other errors (network, etc.)
    const errorMessage = error.message || defaultErrorMessage;
    this.setError(true, errorMessage);
    throw error;
  }

  /**
   * Fetches a specific page using current store parameters
   * @param {number} pageOverride - Optional page number to override currentPage
   * @param {Object} authStore - AuthStore instance para sincronización (opcional)
   * @returns {Promise<void>}
   */
  *fetchPage(pageOverride = null, authStore = null) {
    const params = {
      page: pageOverride !== null ? pageOverride : this.currentPage,
      page_size: this.itemsPerPage,
      ...this.sortParams,
    };
    
    // Add search parameter if it exists
    if (this.searchParams && Object.keys(this.searchParams).length > 0) {
      params.search = this.searchParams;
    }
    
    yield this.fetchWorkspaceList(params, authStore);
  }

  /**
   * Sincroniza currentWorkspace desde AuthStore (cuando hay tenant_uuid en el token)
   * @param {Object} authStore - AuthStore instance
   */
  syncCurrentWorkspaceFromAuthStore(authStore) {
    if (!authStore?.currentWorkspaceUuid || !this.workspaceList || this.workspaceList.length === 0) {
      return;
    }
    
    console.log('🔄 Buscando workspace con UUID del token:', authStore.currentWorkspaceUuid);
    console.log('📋 Lista de workspaces disponibles:', this.workspaceList.map(ws => ({
      name: ws.name || ws.workspaceName || ws.tenant_name,
      workspaceUuid: ws.workspaceUuid,
      tenant_uuid: ws.tenant_uuid,
      tenantUuid: ws.tenantUuid
    })));
    
    const workspace = this.workspaceList.find(ws => {
      // Buscar por todos los posibles campos de UUID (incluyendo workspaceUuid que viene de la API)
      const wsId = ws.tenant_uuid || 
                   ws.tenantUuid || 
                   ws.workspaceUuid || 
                   ws.workspace_uuid ||
                   ws.tenant_id ||
                   ws.id ||
                   ws.uuid;
      const matches = wsId && String(wsId) === String(authStore.currentWorkspaceUuid);
      if (matches) {
        console.log('✅ Workspace encontrado:', {
          name: ws.name || ws.workspaceName || ws.tenant_name,
          wsId,
          tokenUuid: authStore.currentWorkspaceUuid
        });
      }
      return matches;
    });
    
    if (workspace) {
      this.setCurrentWorkspace(workspace, authStore);
      console.log('✅ Workspace sincronizado desde AuthStore (token):', workspace.tenant_name || workspace.name || workspace.workspaceName);
    } else {
      console.warn('⚠️ No se encontró workspace con UUID del token:', authStore.currentWorkspaceUuid);
    }
  }

  /**
   * Helper para obtener UUID del currentWorkspace
   * @returns {string|null} Workspace UUID
   */
  get currentWorkspaceUuid() {
    if (!this.currentWorkspace) return null;
    return this.currentWorkspace.tenant_uuid || 
           this.currentWorkspace.tenantUuid || 
           this.currentWorkspace.workspaceUuid ||
           this.currentWorkspace.workspace_uuid ||
           null;
  }

  /**
   * Fetches workspace list with filters and pagination
   * @param {Object} params - Query parameters
   * @param {Object} authStore - AuthStore instance para sincronización (opcional)
   * @returns {Promise<void>}
   */
  *fetchWorkspaceList(params = {}, authStore = null) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield getWorkspaceList(params);
      const parsed = this.handleApiResponse(response, 'An error occurred while listing workspaces.');
      
      const list = Array.isArray(parsed.body) ? parsed.body : [];
      this.setWorkspaceList(list);
      this.setMeta(parsed.meta || {});
      this.setError(false, "");

      // Si hay tenant_uuid en AuthStore, sincronizar con ese workspace (prioridad)
      if (list.length > 0 && authStore?.currentWorkspaceUuid) {
        this.syncCurrentWorkspaceFromAuthStore(authStore);
      } else if (list.length > 0) {
        // Fallback: Set the first workspace as current by default if:
        // 1. No currentWorkspace is set, OR
        // 2. The current currentWorkspace is not in the new list
        if (!this.currentWorkspace) {
          // If there's no currentWorkspace, use the first one from the list
          this.setCurrentWorkspace(list[0], authStore);
        } else {
          // Check if the current currentWorkspace is in the new list
          const currentId = this.currentWorkspace.tenant_uuid ||
                           this.currentWorkspace.tenantUuid ||
                           this.currentWorkspace.workspaceUuid ||
                           this.currentWorkspace.workspace_uuid ||
                           this.currentWorkspace.tenant_id ||
                           this.currentWorkspace.id ||
                           this.currentWorkspace.uuid;

          const foundInList = list.find(ws => {
            const wsId = ws.tenant_uuid ||
                        ws.tenantUuid ||
                        ws.workspaceUuid ||
                        ws.workspace_uuid ||
                        ws.tenant_id ||
                        ws.id ||
                        ws.uuid;
            return currentId && wsId && String(currentId) === String(wsId);
          });

          // If the currentWorkspace is not in the list, use the first one
          if (!foundInList) {
            this.setCurrentWorkspace(list[0], authStore);
          }
        }
      }

    } catch (error) {
      this.handleApiError(error, 'An error occurred while listing workspaces.');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Fetches a workspace by ID/UUID
   * @param {string} workspaceId - Workspace ID/UUID
   * @returns {Promise<void>}
   */
  *fetchWorkspaceById(workspaceId) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield getWorkspaceById(workspaceId);
      const parsed = this.handleApiResponse(response, 'An error occurred while fetching workspace.');
      
      if (parsed.body) {
        this.setSelectedWorkspace(parsed.body);
        this.setError(false, "");
      } else {
        throw new Error('Workspace not found in response');
      }

    } catch (error) {
      this.handleApiError(error, 'An error occurred while fetching workspace.');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Creates a new workspace
   * @param {Object} workspaceData - Workspace data
   * @param {string} workspaceData.name - Workspace name
   * @param {string} workspaceData.contact_email - Contact email
   * @param {string} workspaceData.contact_name - Contact name
   * @param {string} workspaceData.phone_number - Phone number
   * @param {string} workspaceData.billing_plan - Billing plan
   * @param {number} workspaceData.organization_id - Organization ID
   * @returns {Promise<void>}
   */
  *createWorkspace(workspaceData) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield createWorkspace(workspaceData);
      const parsed = this.handleApiResponse(response, 'An error occurred while creating workspace.');
      
      if (parsed.body) {
        this.setSelectedWorkspace(parsed.body);
        this.setError(false, "");
      } else {
        throw new Error('Workspace not created - no data in response');
      }

    } catch (error) {
      this.handleApiError(error, 'An error occurred while creating workspace.');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Updates an existing workspace
   * @param {Object} workspaceData - Workspace data to update
   * @param {string} workspaceData.tenant_uuid - Tenant UUID (required)
   * @param {string} workspaceData.tenant_name - Workspace name (required)
   * @param {string} workspaceData.contact_email - Contact email (optional)
   * @param {string} workspaceData.address - Address (optional)
   * @returns {Promise<void>}
   */
  *updateWorkspace(workspaceData) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield updateWorkspace(workspaceData);
      const parsed = this.handleApiResponse(response, 'An error occurred while updating workspace.');
      
      if (parsed.body) {
        // After successful update, refetch the complete workspace data
        // to ensure we have all updated fields (address, contact_email, etc.)
        const tenantUuid = workspaceData.tenant_uuid || workspaceData.tenantUuid;
        if (tenantUuid) {
          // Refetch complete workspace data to get all updated fields
          yield this.fetchWorkspaceById(tenantUuid);
        } else {
          // Fallback: update with partial data if we can't refetch
          this.setSelectedWorkspace(parsed.body);
        }
        this.setError(false, "");
      } else {
        throw new Error('Workspace not updated - no data in response');
      }

    } catch (error) {
      this.handleApiError(error, 'An error occurred while updating workspace.');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Uploads a photo for a tenant
   * @param {string} tenantUuid - Tenant UUID
   * @param {File} file - File to upload
   * @returns {Promise<void>}
   */
  *uploadTenantPhoto(tenantUuid, file) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield uploadTenantPhoto(tenantUuid, file);
      const parsed = this.handleApiResponse(response, 'An error occurred while uploading tenant photo.');
      
      // Photo uploaded successfully
      this.setError(false, "");

    } catch (error) {
      this.handleApiError(error, 'An error occurred while uploading tenant photo.');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Activates a workspace by tenant_uuid
   * @param {string} tenantUuid - Tenant UUID
   * @returns {Promise<void>}
   */
  *activateWorkspace(tenantUuid) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield activateWorkspaceService(tenantUuid);
      const parsed = this.handleApiResponse(
        response,
        "An error occurred while activating workspace."
      );

      if (parsed.body) {
        this.setError(false, "");

        // Refetch complete workspace data to ensure we have all fields
        yield this.fetchWorkspaceById(tenantUuid);

        // Refresh the workspace list to reflect the new state (is_active/status)
        yield this.fetchPage();
      }
    } catch (error) {
      this.handleApiError(
        error,
        "An error occurred while activating workspace."
      );
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Deactivates a workspace by tenant_uuid
   * @param {string} tenantUuid - Tenant UUID
   * @returns {Promise<void>}
   */
  *deactivateWorkspace(tenantUuid) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield deactivateWorkspaceService(tenantUuid);
      const parsed = this.handleApiResponse(
        response,
        "An error occurred while deactivating workspace."
      );

      if (parsed.body) {
        this.setError(false, "");

        // Refetch complete workspace data to ensure we have all fields
        yield this.fetchWorkspaceById(tenantUuid);

        // Refresh the workspace list to reflect the new state (is_active/status)
        yield this.fetchPage();
      }
    } catch (error) {
      this.handleApiError(
        error,
        "An error occurred while deactivating workspace."
      );
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Fetches audit list with filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20)
   * @param {string|Array<string>} params.order_by - Fields to order by (default: "created_at")
   * @param {string|Array<string>} params.order_direction - Order direction (ASC/DESC, default: "DESC")
   * @param {Object} params.search - Search object with keyword
   * @param {Object} params.filters - Filter object (action, user_uuid, section_uuid, modified_from, modified_to)
   * @returns {Promise<void>}
   */
  *fetchAuditList(params = {}) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield getAuditList(params);
      const parsed = this.handleApiResponse(response, 'An error occurred while fetching audit list.');

      this.setAuditList(Array.isArray(parsed.body) ? parsed.body : []);
      this.setAuditMeta(parsed.meta || {});
      this.setError(false, "");

    } catch (error) {
      this.handleApiError(error, 'An error occurred while fetching audit list.');
    } finally {
      this.setLoading(false);
    }
  }
}

export default WorkspaceStore;

